<template>
  <div class="container-fluid sale-detail">
    <!-- Loading state -->
    <div v-if="loading" class="loading-state">
      <div class="spinner-border text-primary" role="status">
        <span class="sr-only">Cargando...</span>
      </div>
      <p class="mt-3 text-muted">Cargando pedido...</p>
    </div>

    <div v-else class="sale-detail-content">
      <!-- View header -->
      <div class="view-header my-4">
        <nav class="view-header__breadcrumb" aria-label="breadcrumb">
          <router-link :to="{ name: 'Pedidos Ecommerce' }">Pedidos Ecommerce</router-link>
          <i class="bi bi-chevron-right view-header__breadcrumb-sep"></i>
          <span class="view-header__breadcrumb-current">#{{ order.number || orderId }}</span>
        </nav>

        <div class="view-header__main">
          <div class="view-header__info">
            <h1 class="view-header__title">Detalle de pedido</h1>
            <p class="view-header__subtitle">Información detallada del pedido web.</p>
          </div>

          <div class="view-header__actions">
            <button
              type="button"
              class="btn view-header__btn view-header__btn--primary"
              @click="getPDF(order.id)"
            >
              <i class="bi bi-download"></i>
              Descargar PDF
            </button>
          </div>
        </div>
      </div>

      <!-- Header card -->
      <div class="card invoice-header-card mb-4">
        <div class="invoice-header-main">
          <div class="invoice-type-block">
            <div class="invoice-type-icon">
              <i class="bi bi-bag"></i>
            </div>
            <div class="invoice-type-info">
              <span class="invoice-type-label">Tipo de documento</span>
              <span class="invoice-type-value">Pedido web</span>
              <span v-if="order.number" class="invoice-type-badge">
                #{{ order.number }}
              </span>
            </div>
          </div>

          <div class="invoice-client-block">
            <div class="invoice-client-title">
              <span class="client-title-icon">
                <i class="bi bi-person"></i>
              </span>
              <span>Datos del cliente</span>
            </div>

            <div class="invoice-client-grid">
              <div v-if="order.client" class="client-field">
                <span class="field-label">Nombre / Razón social</span>
                <span class="field-value">{{ order.client.name || '—' }}</span>
              </div>

              <div class="client-field">
                <span class="field-label">
                  <i class="bi bi-calendar3 field-icon"></i>
                  Fecha
                </span>
                <span class="field-value">{{ createdDateFormatted || '—' }}</span>
              </div>

              <div v-if="order.paymethod" class="client-field">
                <span class="field-label">
                  <i class="bi bi-credit-card field-icon"></i>
                  Método de pago
                </span>
                <span class="field-value">{{ order.paymethod }}</span>
              </div>
            </div>
          </div>
        </div>

        <div v-if="hasSecondaryInfo" class="invoice-header-secondary">
          <div v-if="order.billing_type" class="secondary-item">
            <span class="secondary-label">Facturación</span>
            <span class="secondary-value">{{ order.billing_type }}</span>
          </div>
          <div v-if="retireText" class="secondary-item">
            <span class="secondary-label">Método de envío</span>
            <span class="secondary-value">{{ retireText }}</span>
          </div>
        </div>
      </div>

      <!-- Items + totals -->
      <div class="row">
        <!-- Items table -->
        <div class="col-md-8 mb-4 mb-md-0">
          <div class="card items-card mb-4">
            <div class="card-header d-flex justify-content-between align-items-center">
              <div class="card-title-group">
                <span class="card-title-icon">
                  <i class="bi bi-box-seam"></i>
                </span>
                <h5 class="mb-0">Items</h5>
              </div>
              <div v-if="order.details && order.details.length > 0" class="items-summary">
                <span class="items-summary-item">
                  Total de ítems: <strong>{{ order.details.length }}</strong>
                </span>
                <span v-if="totalUnits > 0" class="items-summary-sep">|</span>
                <span v-if="totalUnits > 0" class="items-summary-item">
                  Total de unidades: <strong>{{ totalUnits }}</strong>
                </span>
              </div>
            </div>
            <div class="card-body p-4">
              <div class="table-responsive">
                <table class="table table-sm table-hover items-table mb-0">
                  <thead class="thead-light">
                    <tr>
                      <th>Item</th>
                      <th class="col-small text-center">Cantidad</th>
                      <th class="col-small text-right">Precio unit.</th>
                      <th class="col-small">Variante</th>
                      <th class="col-small text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(d, idx) in order.details" :key="idx">
                      <td>{{ d.name }}</td>
                      <td class="text-center">{{ d.amount }}</td>
                      <td class="text-right">{{ formatoDinamicoMoneda(d.price) }}</td>
                      <td class="text-muted">{{ d.variant || '—' }}</td>
                      <td class="text-right font-weight-bold">
                        {{ formatoDinamicoMoneda(d.amount * d.price) }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div v-if="text" class="card notes-card">
            <div class="card-header">
              <div class="card-title-group">
                <span class="card-title-icon">
                  <i class="bi bi-sticky"></i>
                </span>
                <h6 class="mb-0">Nota del cliente</h6>
              </div>
            </div>
            <div class="card-body">
              <p class="mb-0 note-text">{{ text }}</p>
            </div>
          </div>
        </div>

        <!-- Totals + client -->
        <div class="col-md-4">
          <div class="card totals-card mb-4">
            <div class="card-header">
              <div class="card-title-group">
                <span class="card-title-icon">
                  <i class="bi bi-cash-coin"></i>
                </span>
                <h5 class="mb-0">Totales de la orden</h5>
              </div>
            </div>
            <div class="card-body">
              <div class="total-row">
                <span class="label">Subtotal</span>
                <span class="value">{{ formatoDinamicoMoneda(order.subtotal) }}</span>
              </div>

              <div class="total-row">
                <span class="label">Envío</span>
                <span class="value">{{ formatoDinamicoMoneda(order.shipping_fee) }}</span>
              </div>

              <div class="totals-divider"></div>

              <div class="total-row total-final">
                <span class="label">Total</span>
                <span class="value">{{ formatoDinamicoMoneda(order.total) }}</span>
              </div>
            </div>
          </div>

          <div v-if="order.client" class="card notes-card mb-4">
            <div class="card-header">
              <div class="card-title-group">
                <span class="card-title-icon">
                  <i class="bi bi-person-vcard"></i>
                </span>
                <h6 class="mb-0">Datos del titular</h6>
              </div>
            </div>
            <div class="card-body">
              <div class="client-grid">
                <div class="client-field">
                  <span class="field-label">Nombre</span>
                  <span class="field-value">{{ order.client.name || '—' }}</span>
                </div>
                <div v-if="order.client.email" class="client-field">
                  <span class="field-label">
                    <i class="bi bi-envelope field-icon"></i>
                    Email
                  </span>
                  <span class="field-value">{{ order.client.email }}</span>
                </div>
                <div v-if="order.client.phone" class="client-field">
                  <span class="field-label">
                    <i class="bi bi-telephone field-icon"></i>
                    Teléfono
                  </span>
                  <span class="field-value">{{ order.client.phone }}</span>
                </div>
                <div v-if="order.client.address" class="client-field">
                  <span class="field-label">
                    <i class="bi bi-geo-alt field-icon"></i>
                    Dirección
                  </span>
                  <span class="field-value">{{ order.client.address }}</span>
                </div>
                <div v-if="order.client.city" class="client-field">
                  <span class="field-label">Localidad</span>
                  <span class="field-value">{{ order.client.city }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import formatDateMixin from "../../mixins/formatDateMixin";
import formatMoneyMixin from "../../mixins/formatMoneyMixin";
import myaxios from 'axios';

export default {
  props: ['orderId'],
  mixins: [formatDateMixin, formatMoneyMixin],
  data() {
    return {
      loading: true,
      text: '',
      order: {},
    };
  },
  computed: {
    createdDateFormatted() {
      if (!this.order.created_at) return '';
      return new Date(this.order.created_at).toLocaleDateString('es-AR');
    },
    retireText() {
      const r = this.order.retire_data;
      if (!r || !r.retire_name) return '';
      return r.retire_dni ? `${r.retire_name} - DNI: ${r.retire_dni}` : r.retire_name;
    },
    hasSecondaryInfo() {
      return !!(this.order.billing_type || this.retireText);
    },
    totalUnits() {
      if (!Array.isArray(this.order.details)) return 0;
      return this.order.details.reduce((sum, item) => {
        const q = Number(item.amount) || 0;
        return sum + q;
      }, 0);
    },
  },
  mounted() {
    this.getOrder();
  },
  methods: {
    getOrder() {
      this.loading = true;
      this.$http
        .get('/api/ecom/orders/' + this.orderId)
        .then(response => {
          this.order = response.data.data;
          this.text = response.data.data.notes;
        })
        .catch(error => {
          console.error('Error al cargar pedido:', error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    getPDF(id) {
      this.$http
        .get('/api/ecom/orders/regeneratePDF/' + id)
        .then(response => {
          const uninterceptedAxiosInstance = myaxios.create();
          uninterceptedAxiosInstance
            .get(response.data.data, { responseType: 'blob' })
            .then(res => {
              const blob = new Blob([res.data], { type: 'application/pdf' });
              const link = document.createElement('a');
              link.href = URL.createObjectURL(blob);
              link.download = id + '.pdf';
              link.click();
              URL.revokeObjectURL(link.href);
            })
            .catch(error => {
              console.error('Error al descargar PDF:', error);
            });
        })
        .catch(error => {
          console.error('Error al regenerar PDF:', error);
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.client-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.75rem;

  .client-field {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
}
</style>
